import React, { useState, useMemo, useCallback, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import DragHandleIcon from '@mui/icons-material/DragHandle'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import { useDispatch } from 'react-redux'
import { getTasksRoutine, updateTaskRoutine } from 'features/taskManagement/ducks/actions'
import TaskAssigneeQuickEdit from 'features/taskManagement/components/TaskAssigneeQuickEdit'
import TaskDeadlineQuickEdit from 'features/compliance/TaskDeadlineQuickEdit'
import TaskPriorityQuickEdit from 'features/taskManagement/components/TaskPriorityQuickEdit'
import TaskContractorQuickEdit from 'features/taskManagement/components/TaskContractorQuickEdit'
import TaskExternalQuickEdit from 'features/taskManagement/components/TaskExternalQuickEdit'
import TaskFormQuickEdit from 'features/taskManagement/components/TaskFormQuickEdit'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import TaskTypeBadge from 'features/taskManagement/components/TaskTypeBadge'
import TaskCadenceBadge from 'features/taskManagement/components/TaskCadenceBadge'
import { useApproveFromPill, canApproveFromPill } from 'features/taskManagement/useApproveFromPill'
import NextOccurrenceBadge from 'features/taskManagement/NextOccurrenceBadge'
import TaskPeopleAvatars from 'features/taskManagement/components/TaskPeopleAvatars'
import { getTask, updateApprovers } from 'services/taskService'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const EMPTY_PEOPLE = []
const COL_COUNT = 10
const PAGE = 30

const priorityConfig = {
  low: { label: PRIORITY_META.low.label, color: PRIORITY_META.low.fg, bg: PRIORITY_META.low.bg, icon: KeyboardArrowDownIcon },
  medium: { label: PRIORITY_META.medium.label, color: PRIORITY_META.medium.fg, bg: PRIORITY_META.medium.bg, icon: DragHandleIcon },
  high: { label: PRIORITY_META.high.label, color: PRIORITY_META.high.fg, bg: PRIORITY_META.high.bg, icon: KeyboardArrowUpIcon },
  critical: { label: PRIORITY_META.critical.label, color: PRIORITY_META.critical.fg, bg: PRIORITY_META.critical.bg, icon: KeyboardDoubleArrowUpIcon }
}

const openTask = (id) => redirect(PRIVATE_PATHS.taskDetails.replace(':id', id))

const PriorityChip = ({ priority }) => {
  const cfg = priorityConfig[priority]
  if (!cfg) return <MutedDash>Not set</MutedDash>
  const Icon = cfg.icon
  return (
    <PriorityPill $color={cfg.color}>
      <Icon style={{ fontSize: 15 }} />
      {cfg.label}
    </PriorityPill>
  )
}


const TaskRow = React.memo(function TaskRow ({ task, isSelected, isExpanded, onToggleSelect, onToggleExpand, onPatch }) {
  const status = taskStatusMeta(task.status)
  const isOverdue = task.isOverdue && task.status !== 'completed'
  const bpId = task.building?.id || task.buildingPassportId
  const unitList = task.units && task.units.length > 0 ? task.units : (task.unit ? [task.unit] : [])
  const onSaved = useCallback(() => onPatch(task.id), [onPatch, task.id])
  const stop = useCallback((e) => e.stopPropagation(), [])
  const [approveFromPill, approveConfirmNode] = useApproveFromPill()

  return (
    <>
      <TableRow $isOverdue={isOverdue} $selected={isSelected} onClick={() => openTask(task.id)}>
        <Td $w="36px" $center onClick={stop}>
          <ExpandBtn
            type="button"
            aria-label={isExpanded ? 'Hide details' : 'Show details'}
            aria-expanded={isExpanded}
            $open={isExpanded}
            onClick={() => onToggleExpand(task.id)}
          >
            {isExpanded ? <ExpandMoreRoundedIcon /> : <ChevronRightRoundedIcon />}
          </ExpandBtn>
        </Td>
        <Td $w="40px" $checkbox onClick={stop}>
          <Checkbox type="checkbox" checked={isSelected} onChange={() => onToggleSelect?.(task.id)} />
        </Td>
        <Td $grow="240px">
          <TaskName>{task.title}</TaskName>
          <TaskSubRow>
            <TaskTypeBadge task={task} />
            <TaskCadenceBadge task={task} />
            <NextOccurrenceBadge task={task} compact />
          </TaskSubRow>

          {(task.blockedBy?.length > 0
            || task.requiresSignOff || (task.status === 'on_hold' && task.holdReason)) && (
            <TaskFlags>
              {task.blockedBy?.length > 0 && (
                <Flag $tone="amber" title={`Blocked by ${task.blockedBy.length} task${task.blockedBy.length === 1 ? '' : 's'}`}>
                  Blocked{task.blockedBy.length > 1 ? ` ·${task.blockedBy.length}` : ''}
                </Flag>
              )}
              {task.requiresSignOff && (task.signedOffAt
                ? <Flag $tone="green" title="Signed off">Signed off</Flag>
                : <Flag $tone="amber" title="Sign-off required">Sign-off</Flag>)}
              {task.status === 'on_hold' && task.holdReason && (
                <Flag $tone="grey" title={task.holdReason}>On hold</Flag>
              )}
            </TaskFlags>
          )}
        </Td>
        <Td $w="160px">
          {task.building ? (
            <BuildingCell>
              <ApartmentOutlinedIcon />
              <span>{task.building.name}</span>
            </BuildingCell>
          ) : <MutedDash>No building</MutedDash>}
        </Td>
        <Td $w="96px">
          {task.taskDomain ? (
            <DomainChip $colour={task.taskDomain.colour} title={task.taskDomain.name}>
              <DomainSwatch $colour={task.taskDomain.colour} />
              {task.taskDomain.short || task.taskDomain.name}
            </DomainChip>
          ) : <MutedDash>No domain</MutedDash>}
        </Td>
        <Td $w="150px">
          {canApproveFromPill(task) ? (
            <StatusBadge
              $color={status.color}
              $bg={status.bg}
              $clickable
              role="button"
              tabIndex={0}
              onClick={(e) => approveFromPill(task, e)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); approveFromPill(task, e) } }}
              title="Click to approve and complete this task"
            >
              {status.label}<PillTick aria-hidden="true">✓</PillTick>
            </StatusBadge>
          ) : (
            <StatusBadge $color={status.color} $bg={status.bg}>{status.label}</StatusBadge>
          )}
          {approveConfirmNode}
        </Td>
        <Td onClick={stop}><TaskPeopleAvatars task={task} emptyLabel="Unassigned" /></Td>
        <Td $w="100px"><PriorityChip priority={task.priority} /></Td>
        <Td $w="110px">
          {task.deadline
            ? <DeadlineCell $isOverdue={isOverdue}>{moment(task.deadline).format('DD MMM YY')}</DeadlineCell>
            : <MutedDash>No deadline</MutedDash>}
        </Td>
        <Td $w="72px" onClick={stop}>
          <ActionButtons>
            {task.status === 'awaiting_approval' && task.permissions?.canApprove && (
              <TableApproveBtn onClick={() => openTask(task.id)} title="Review &amp; Approve">Approve</TableApproveBtn>
            )}
            <ActionBtn title="Open task" onClick={() => openTask(task.id)}><EditOutlinedIcon /></ActionBtn>
          </ActionButtons>
        </Td>
      </TableRow>

      {isExpanded && (
        <DetailRow onClick={stop}>
          <Td colSpan={COL_COUNT} $detail>
            <DetailGrid>
              <DetailField>
                <DetailLabel>Assignees</DetailLabel>
                <TaskAssigneeQuickEdit task={task} bpId={bpId} onSaved={onSaved} />
              </DetailField>
              <DetailField>
                <DetailLabel>Approvers</DetailLabel>
                <TaskAssigneeQuickEdit
                  task={task}
                  bpId={bpId}
                  current={task.approvers || EMPTY_PEOPLE}
                  onSaveIds={(ids, scope) => updateApprovers({ taskId: task.id, approverIds: ids, scope })}
                  title="Set approvers"
                  emptyLabel="Set approver"
                  warnIds={(task.assignees || []).map(a => a.id)}
                  warnMessage="This person is also an assignee on this task. You can still save."
                  onSaved={onSaved}
                />
              </DetailField>
              <DetailField>
                <DetailLabel>External people</DetailLabel>
                <TaskExternalQuickEdit task={task} onSaved={onSaved} />
              </DetailField>
              <DetailField>
                <DetailLabel>Contractor</DetailLabel>
                <TaskContractorQuickEdit task={task} onSaved={onSaved} />
              </DetailField>
              <DetailField>
                <DetailLabel>Form</DetailLabel>
                <TaskFormQuickEdit task={task} onSaved={onSaved}>
                  {task.formTemplate
                    ? <FormChip title={task.formTemplate.name}>{task.formTemplate.name}</FormChip>
                    : <SetFormChip>+ Set form</SetFormChip>}
                </TaskFormQuickEdit>
              </DetailField>
              <DetailField>
                <DetailLabel>Priority</DetailLabel>
                <TaskPriorityQuickEdit task={task} onSaved={onSaved} />
              </DetailField>
              <DetailField>
                <DetailLabel>Deadline</DetailLabel>
                <InlineRow>
                  {task.deadline
                    ? <DeadlineCell $isOverdue={isOverdue}>{moment(task.deadline).format('DD MMM YY')}</DeadlineCell>
                    : <MutedDash>No deadline</MutedDash>}
                  <TaskDeadlineQuickEdit task={task} onSaved={onSaved} />
                </InlineRow>
              </DetailField>
              <DetailField>
                <DetailLabel>Unit</DetailLabel>
                {unitList.length > 0 ? (
                  <UnitCell>
                    <MeetingRoomOutlinedIcon />
                    <span>{unitList[0].name}</span>
                    {unitList.length > 1 && <UnitBadge>+{unitList.length - 1}</UnitBadge>}
                  </UnitCell>
                ) : <MutedDash>No unit</MutedDash>}
              </DetailField>
              <DetailField>
                <DetailLabel>Team</DetailLabel>
                {task.team ? (
                  <TeamCell><GroupOutlinedIcon /><span>{task.team.name}</span></TeamCell>
                ) : <MutedDash>No team</MutedDash>}
              </DetailField>
              <DetailField>
                <DetailLabel>Progress</DetailLabel>
                {task.progress > 0 ? (
                  <ProgressCell>
                    <MiniProgressBar><MiniProgressFill $progress={task.progress} $color={status.color} /></MiniProgressBar>
                    <span>{task.progress}%</span>
                  </ProgressCell>
                ) : <MutedDash>Not started</MutedDash>}
              </DetailField>
              <DetailField>
                <DetailLabel>Created</DetailLabel>
                {task.createdAt ? <CreatedCell>{moment(task.createdAt).format('DD MMM YY')}</CreatedCell> : <MutedDash>Not recorded</MutedDash>}
              </DetailField>
            </DetailGrid>
          </Td>
        </DetailRow>
      )}
    </>
  )
})

const TaskTable = ({ tasks, searchValue, total, loadingAll = false, resetKey = '', selectedTaskIds = [], onToggleSelect, onToggleSelectAll }) => {
  const dispatch = useDispatch()
  const [sortField, setSortField] = useState('deadline')
  const [sortDir, setSortDir] = useState('asc')
  const [expandedIds, setExpandedIds] = useState(() => new Set())
  const [collapsedIds, setCollapsedIds] = useState(() => new Set())
  const [expandAll, setExpandAll] = useState(false)
  const [visibleCount, setVisibleCount] = useState(PAGE)
  const [scrollEl, setScrollEl] = useState(null)

  const patchTaskInList = useCallback(async (taskId) => {
    try {
      const res = await getTask(taskId)
      dispatch(updateTaskRoutine.success(res.data?.data || res.data))
    } catch {
      dispatch(getTasksRoutine({ stable: true }))
    }
  }, [dispatch])

  const toggleExpand = useCallback((id) => {
    const setter = expandAll ? setCollapsedIds : setExpandedIds
    setter(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [expandAll])

  const isRowExpanded = useCallback(
    (id) => (expandAll ? !collapsedIds.has(id) : expandedIds.has(id)),
    [expandAll, collapsedIds, expandedIds]
  )

  const selectedSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds])

  const sortedTasks = useMemo(() => {
    const sorted = [...tasks]
    sorted.sort((a, b) => {
      let valA, valB
      switch (sortField) {
        case 'title': valA = a.title?.toLowerCase() || ''; valB = b.title?.toLowerCase() || ''; break
        case 'status': valA = a.status || ''; valB = b.status || ''; break
        case 'deadline':
          valA = a.deadline ? new Date(a.deadline).getTime() : Infinity
          valB = b.deadline ? new Date(b.deadline).getTime() : Infinity
          break
        case 'priority': {
          const order = { urgent: 0, high: 1, medium: 2, low: 3 }
          valA = order[a.priority] ?? 4; valB = order[b.priority] ?? 4
          break
        }
        case 'building': valA = a.building?.name?.toLowerCase() || ''; valB = b.building?.name?.toLowerCase() || ''; break
        default: valA = a[sortField] || ''; valB = b[sortField] || ''
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1
      if (valA > valB) return sortDir === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [tasks, sortField, sortDir])

  const grouped = useMemo(() => {
    const projects = {}
    const noProject = []
    sortedTasks.forEach(task => {
      if (task.project) {
        if (!projects[task.project.id]) projects[task.project.id] = { project: task.project, tasks: [] }
        projects[task.project.id].tasks.push(task)
      } else {
        noProject.push(task)
      }
    })
    return { projects: Object.values(projects), ungrouped: noProject }
  }, [sortedTasks])

  const loadedCount = sortedTasks.length
  // The true total (may exceed what's loaded so far while the parent is still
  // fetching pages in the background). Falls back to what's loaded.
  const totalCount = Number.isFinite(total) && total > loadedCount ? total : loadedCount
  // Reset the visible slice to page 1 ONLY when the filter/search/sort actually
  // changes — never when the background loader merely appends more tasks
  // (which used to snap the table back to 30 repeatedly).
  useEffect(() => { setVisibleCount(PAGE) }, [resetKey, sortField, sortDir])

  const visible = useMemo(() => {
    let budget = visibleCount
    const projects = []
    for (const p of grouped.projects) {
      if (budget <= 0) break
      const take = p.tasks.slice(0, budget)
      budget -= take.length
      projects.push({ project: p.project, tasks: take, allTasks: p.tasks })
    }
    const ungrouped = budget > 0 ? grouped.ungrouped.slice(0, budget) : []
    return { projects, ungrouped }
  }, [grouped, visibleCount])

  const shownCount = Math.min(visibleCount, loadedCount)
  // Only "more to show" if there are loaded-but-unrendered rows, or the parent
  // is still fetching pages. Never key this off the (server) total — with a
  // client-side search/category the displayed set is smaller, and doing so
  // would leave hasMore stuck true forever with nothing left to render.
  const hasMore = shownCount < loadedCount || loadingAll
  const waitingForServer = loadingAll && shownCount >= loadedCount

  const toggleExpandAll = () => {
    if (expandAll) {
      setExpandAll(false)
      setCollapsedIds(new Set())
      setExpandedIds(new Set())
    } else {
      setExpandAll(true)
      setCollapsedIds(new Set())
    }
  }

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortField(field); setSortDir('asc') }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <UnfoldMoreIcon />
    return sortDir === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />
  }

  const getProjectProgress = (projectTasks) => {
    const total = projectTasks.length
    if (total === 0) return ''
    const completed = projectTasks.filter(t => t.status === 'completed').length
    const inProgress = projectTasks.filter(t => t.status === 'in_progress').length
    const parts = []
    if (inProgress > 0) parts.push(`${Math.round(inProgress / total * 100)}% in progress`)
    if (completed > 0) parts.push(`${Math.round(completed / total * 100)}% completed`)
    return parts.join(', ') || `${total} tasks`
  }

  const renderRow = (task) => (
    <TaskRow
      key={task.id}
      task={task}
      isSelected={selectedSet.has(task.id)}
      isExpanded={isRowExpanded(task.id)}
      onToggleSelect={onToggleSelect}
      onToggleExpand={toggleExpand}
      onPatch={patchTaskInList}
    />
  )

  const allChecked = sortedTasks.length > 0 && sortedTasks.every(t => selectedSet.has(t.id))

  return (
    <TableWrapper>
      <TableTools>
        <ToolLeft>
          <ToolHint>Click a row&apos;s arrow to see assignees, approvers, contractors and more.</ToolHint>
          {totalCount > 0 && (
            <ToolCount>
              Showing {shownCount.toLocaleString()} of {totalCount.toLocaleString()}
              {loadingAll && <LoadingDot title="Still loading the rest in the background"> · loading…</LoadingDot>}
            </ToolCount>
          )}
        </ToolLeft>
        <HeightToggle
          type="button"
          onClick={toggleExpandAll}
          aria-pressed={expandAll}
          title={expandAll ? 'Collapse every row' : 'Open every row. New rows stay open as you scroll'}
        >
          {expandAll ? <KeyboardArrowUpIcon style={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
          {expandAll ? 'Collapse all' : 'Expand all'}
        </HeightToggle>
      </TableTools>
      <TableScroll ref={setScrollEl}>
        <StyledTable>
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '44px' }} />
            <col />
            <col style={{ width: '148px' }} />
            <col style={{ width: '64px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '132px' }} />
            <col style={{ width: '92px' }} />
            <col style={{ width: '92px' }} />
            <col style={{ width: '140px' }} />
          </colgroup>
          <thead>
            <tr>
              <Th $checkbox />
              <Th $checkbox>
                <Checkbox
                  type="checkbox"
                  checked={allChecked}
                  onChange={() => onToggleSelectAll?.(allChecked ? [] : sortedTasks.map(t => t.id))}
                />
              </Th>
              <Th onClick={() => handleSort('title')}>Task <SortIcon field="title" /></Th>
              <Th onClick={() => handleSort('building')}>Building Passport <SortIcon field="building" /></Th>
              <Th>Domain</Th>
              <Th onClick={() => handleSort('status')}>Status <SortIcon field="status" /></Th>
              <Th>People</Th>
              <Th onClick={() => handleSort('priority')}>Priority <SortIcon field="priority" /></Th>
              <Th onClick={() => handleSort('deadline')}>Deadline <SortIcon field="deadline" /></Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {visible.projects.map(({ project, tasks: projectTasks, allTasks }) => (
              <React.Fragment key={`project-${project.id}`}>
                <ProjectRow>
                  <Td colSpan={COL_COUNT}>
                    <ProjectLabel>
                      <FolderOutlinedIcon style={{ fontSize: 16, color: project.color || '#33becc', opacity: 0.8 }} />
                      <strong>{project.name}</strong>
                      <ProjectMeta>{getProjectProgress(allTasks)}</ProjectMeta>
                    </ProjectLabel>
                  </Td>
                </ProjectRow>
                {projectTasks.map(renderRow)}
              </React.Fragment>
            ))}
            {visible.ungrouped.length > 0 && visible.projects.length > 0 && (
              <ProjectRow>
                <Td colSpan={COL_COUNT}>
                  <ProjectLabel>
                    <FolderOutlinedIcon style={{ fontSize: 16, color: '#9ca3af', opacity: 0.8 }} />
                    <strong>No Project</strong>
                  </ProjectLabel>
                </Td>
              </ProjectRow>
            )}
            {visible.ungrouped.map(renderRow)}
            {sortedTasks.length === 0 && (
              <tr>
                <Td colSpan={COL_COUNT} style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                  No tasks found
                </Td>
              </tr>
            )}
          </tbody>
        </StyledTable>
        <InfiniteScrollSentinel
          hasMore={hasMore}
          loading={waitingForServer}
          root={scrollEl}
          onLoadMore={() => setVisibleCount(c => Math.min(c + PAGE, loadedCount + PAGE))}
          label="Loading more tasks…"
        />
      </TableScroll>
    </TableWrapper>
  )
}

export default TaskTable

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
  height: calc(100vh - 300px);
  min-height: 440px;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 768px) { display: none; }
`

const TableScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const TableTools = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`

const ToolLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
`

const ToolHint = styled.span`
  font-size: 12px;
  color: #94a3b8;
`

const ToolCount = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
  white-space: nowrap;
`

const LoadingDot = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
`

const HeightToggle = styled.button`
  font: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px 5px 8px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 700;
  color: #475569;
  background: white;
  border: 1px solid #e2e8f0;
  transition: background-color 140ms ease, border-color 140ms ease;
  &:hover { background: #f8fafc; border-color: #cbd5e1; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:disabled:hover { background: white; border-color: #e2e8f0; }
`

const StyledTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  position: sticky;
  top: 0;
  background: white;
  text-align: left;
  padding: ${({ $checkbox }) => ($checkbox ? '12px 8px 12px 14px' : '12px 12px')};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  border-bottom: 2px solid ${({ theme }) => theme.colors.lightGrey};
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  z-index: 1;
  ${({ $w }) => $w && `width: ${$w};`}

  svg { font-size: 14px; vertical-align: middle; margin-left: 2px; opacity: 0.5; }
  &:hover { color: ${({ theme }) => theme.colors.haiti}; svg { opacity: 1; } }
`

const Td = styled.td`
  padding: ${({ $checkbox, $detail }) => ($checkbox ? '9px 8px 9px 14px' : $detail ? '0' : '9px 12px')};
  color: ${({ theme }) => theme.colors.haiti};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  ${({ $detail }) => !$detail && 'white-space: nowrap; overflow: hidden;'}
  ${({ $center }) => $center && 'text-align: center;'}
`

const TableRow = styled.tr`
  cursor: pointer;
  transition: background 0.12s;
  ${({ $isOverdue }) => $isOverdue && 'background: #fff5f4;'}
  ${({ $selected }) => $selected && 'background: #e6f7f9;'}

  &:hover {
    background: ${({ $isOverdue, $selected, theme }) =>
      $selected ? '#e6f7f9' : $isOverdue ? '#ffedeb' : theme.colors.lightGrey};
  }
`

const DetailRow = styled.tr`
  background: #fbfcfe;
  cursor: default;
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 14px 20px;
  padding: 16px 18px 18px 62px;
  border-left: 3px solid #33becc;
`

const DetailField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`

const DetailLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
`

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ExpandBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;
  svg { font-size: 20px; }
  &:hover { background: #e6f7f9; color: #2aa6b3; }
  ${({ $open }) => $open && 'color: #2aa6b3;'}
`

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.haiti || '#0C1229'};
`

const ProjectRow = styled.tr`
  background: ${({ theme }) => theme.colors.lightGrey};
  td { padding: 8px 14px; border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGrey}; }
`

const ProjectLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ProjectMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-left: 8px;
`

const TaskName = styled.div`
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FLAG_TONES = {
  purple: { c: '#6d28d9', b: '#ede9fe' },
  amber:  { c: '#d97706', b: '#fef3c7' },
  blue:   { c: '#33becc', b: '#e6f7f9' },
  green:  { c: '#15803d', b: '#dcfce7' },
  grey:   { c: '#475569', b: '#e2e8f0' },
}

const TaskFlags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 5px;
`

const TaskSubRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`

const Flag = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 7px;
  border-radius: 999px;
  white-space: nowrap;
  color: ${({ $tone }) => (FLAG_TONES[$tone] || FLAG_TONES.grey).c};
  background: ${({ $tone }) => (FLAG_TONES[$tone] || FLAG_TONES.grey).b};
`

const ProgressCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  span { font-size: 11px; font-weight: 600; color: ${({ theme }) => theme.colors.grey?.[800] || '#999'}; min-width: 28px; }
`

const MiniProgressBar = styled.div`
  width: 50px;
  height: 4px;
  background: ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 2px;
  overflow: hidden;
`

const MiniProgressFill = styled.div`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $color }) => $color || '#059669'};
  border-radius: 2px;
`

const BuildingCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  svg { font-size: 15px; opacity: 0.5; flex-shrink: 0; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ $colour }) => $colour || '#475569'};
  background: ${({ $colour }) => `${$colour || '#64748b'}14`};
`

const DomainSwatch = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $colour }) => $colour || '#64748b'};
`

const MutedDash = styled.span`
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  font-size: 12.5px;
`

const UnitCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #7c3aed;
  svg { font-size: 15px; opacity: 0.6; flex-shrink: 0; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`

const UnitBadge = styled.span`
  && {
    font-size: 10px;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 8px;
    background: #ede9fe;
    color: #7c3aed;
    white-space: nowrap;
    flex-shrink: 0;
  }
`

const TeamCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #0d7377;
  svg { font-size: 15px; opacity: 0.6; flex-shrink: 0; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`

const FormChip = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  color: #5b21b6;
  background: #ede9fe;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
  display: inline-block;
`

const SetFormChip = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  color: #64748b;
  background: #f1f5f9;
  border: 1px dashed #cbd5e1;
  display: inline-block;
`

const PriorityPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $color }) => $color};
`

const DeadlineCell = styled.span`
  font-weight: 500;
  color: ${({ $isOverdue }) => $isOverdue ? '#dc2626' : 'inherit'};
  ${({ $isOverdue }) => $isOverdue && 'background: #fee2e2; padding: 2px 6px; border-radius: 4px;'}
`

const CreatedCell = styled.span`
  font-weight: 500;
  color: #6b7280;
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color || '#6b7280'};
  background: ${({ $bg }) => $bg || '#f3f4f6'};
  white-space: nowrap;
  ${({ $clickable }) => $clickable ? `
    cursor: pointer;
    &:hover { filter: brightness(0.95); }
    &:focus-visible { outline: 2px solid currentColor; outline-offset: 1px; }
  ` : ''}
`

const PillTick = styled.span`
  font-weight: 800;
  line-height: 1;
`

const TableApproveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;
  &:hover { background: ${({ theme }) => theme.colors.haiti}; box-shadow: 0 2px 6px rgba(41, 152, 163, 0.25); }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-end;
`

const ActionBtn = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  color: ${({ $color, theme }) => $color || theme.colors.haiti};
  svg { font-size: 16px; }
  &:hover { background: ${({ theme }) => theme.colors.mediumGrey}; }
`
